import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  CircularProgress,
  Typography,
  Button
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import ResearchJobsTable from './ResearchJobsTable';
import ResearchResults from './ResearchResults';
import { useAuth } from '../../context/AuthContext';
import { apiGet, apiPost, apiDelete } from '../../utils/apiUtils';
import { createLogger } from '../../utils/debugUtils';

const logger = createLogger('ResearchAgentActivityHistory');

/**
 * ResearchAgentActivityHistory Component
 * Dedicated page for viewing research job history and results
 */
const ResearchAgentActivityHistory = () => {
  useAuth(); // Keep auth context for future use
  
  // Removed unused jobsListRefreshRef
  
  // State for jobs and error handling
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState(null);
  
  // Results view state
  const [viewingResults, setViewingResults] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [jobResults, setJobResults] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  
  /**
   * Fetch jobs from the API
   */
  const fetchJobs = useCallback(async () => {
    try {
      const response = await apiGet('/api/research/jobs');
      
      // Check if response is a Response object and parse it
      if (response instanceof Response) {
        if (!response.ok) {
          throw new Error(`API returned status ${response.status}`);
        }
        const data = await response.json();
        if (Array.isArray(data)) {
          // Normalize job data and ensure createdAt is directly accessible at top level
          const normalizedJobs = data.map(job => ({
            ...job,
            // Add any custom mappings or transformations here if needed
          }));
          
          
          setJobs(normalizedJobs);
        } else {
          logger.warn('Invalid jobs data format (not an array)', data);
          setJobs([]);
        }
      } else if (Array.isArray(response)) {
        // Handle case where apiGet might already return parsed data
        setJobs(response);
      } else {
        logger.warn('Invalid jobs data format', response);
        setJobs([]);
      }
    } catch (error) {
      logger.error('Failed to fetch jobs:', error);
      setJobs([]);
      setError('Failed to load research jobs. Please try again later.');
    }
  }, []);


  // Fetch jobs on component mount and set up auto-refresh
  useEffect(() => {
    // Initial fetch
    fetchJobs();
    
    // Set up auto-refresh every 5 seconds
    const REFRESH_INTERVAL = 5000; // 5 seconds in milliseconds
    const intervalId = setInterval(() => {
      fetchJobs();
    }, REFRESH_INTERVAL);
    
    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [fetchJobs]);

  /**
   * Handle pausing a job
   */
  const handlePauseJob = async (job) => {
    try {
      const response = await apiPost(`/api/research/jobs/${job.jobId}/pause`);
      
      // Check if response is a Response object and handle it
      if (response instanceof Response) {
        if (!response.ok) {
          throw new Error(`API returned status ${response.status}`);
        }
        // Parse the response if needed
        await response.text();
      }
      
      await fetchJobs(); // Refresh jobs after action
    } catch (error) {
      logger.error(`Failed to pause job ${job.jobId}:`, error);
      setError(`Failed to pause job: ${job.name}. Please try again.`);
    }
  };

  /**
   * Handle resuming a job
   */
  const handleResumeJob = async (job) => {
    try {
      const response = await apiGet(`/api/research/jobs/${job.jobId}/resume`, { method: 'POST' });
      
      // Check if response is a Response object and handle it
      if (response instanceof Response) {
        if (!response.ok) {
          throw new Error(`API returned status ${response.status}`);
        }
        // Parse the response if needed
        await response.text();
      }
      
      await fetchJobs(); // Refresh jobs after action
    } catch (error) {
      logger.error(`Failed to resume job ${job.jobId}:`, error);
      setError(`Failed to resume job: ${job.name}. Please try again.`);
    }
  };

  /**
   * Handle deleting a job
   */
  const handleDeleteJob = async (job) => {
    if (window.confirm(`Are you sure you want to delete the job: ${job.name}?`)) {
      try {
        // Use apiDelete instead of apiGet for proper DELETE request
        const response = await apiDelete(`/api/research/jobs/${job.jobId}`);
        
        // Check if response is a Response object and handle it
        if (response instanceof Response) {
          if (!response.ok) {
            throw new Error(`API returned status ${response.status}`);
          }
          // Parse the response if needed
          await response.text();
        }
        
        logger.info(`Successfully deleted job ${job.jobId}`);
        // Refresh jobs after successful deletion
        await fetchJobs();
      } catch (error) {
        logger.error(`Failed to delete job ${job.jobId}:`, error);
        setError(`Failed to delete job: ${job.name}. Please try again.`);
      }
    }
  };

  /**
   * Fetch job results when a job is selected for viewing
   */
  const handleViewResults = async (job) => {
    const jobId = job.jobId;
    if (!jobId) return;
    
    setSelectedJobId(jobId);
    setResultsLoading(true);
    setViewingResults(true);
    
    try {
      const response = await apiGet(`/api/research/jobs/${jobId}/results`);
      
      // Check if response is a Response object and parse it
      let websiteResults = [];
      let responseData;
      
      if (response instanceof Response) {
        if (!response.ok) {
          throw new Error(`API returned status ${response.status}`);
        }
        responseData = await response.json();
      } else {
        // Handle case where apiGet might already return parsed data
        responseData = response;
      }
      
      // Handle different response formats
      if (Array.isArray(responseData)) {
        // API returns an array of website results
        websiteResults = responseData;
        logger.info(`Loaded ${websiteResults.length} results for job ${jobId}`);
        
        // Format the results as expected by ResearchResults component
        const formattedResults = {
          websites: websiteResults,
          completedAt: job.completedAt || new Date().toISOString()
        };
        
        logger.info(`Formatted results for ResearchResults component`, {
          websitesCount: formattedResults.websites.length
        });
        
        setJobResults(formattedResults);
      } else if (responseData && responseData.websites) {
        // API directly returns the expected format with websites array
        logger.info(`Loaded ${responseData.websites.length} results for job ${jobId}`);
        setJobResults(responseData);
      } else {
        // No valid results
        logger.warn(`No valid results found for job ${jobId}`, responseData);
        setJobResults({
          websites: [],
          completedAt: job.completedAt || new Date().toISOString()
        });
      }
    } catch (error) {
      logger.error(`Error loading results for job ${jobId}:`, error);
      setJobResults({
        websites: [],
        completedAt: job.completedAt || new Date().toISOString()
      });
      setError(`Failed to load research results. Please try again later.`);
    } finally {
      setResultsLoading(false);
    }
  };

  /**
   * Go back from results view to jobs list
   */
  const handleBackToJobs = () => {
    setViewingResults(false);
    setSelectedJobId(null);
    setJobResults(null);
  };

  // Clear any errors when component unmounts or when changing views
  useEffect(() => {
    return () => setError(null);
  }, [viewingResults]);

  // Clear error when user returns to see the component
  useEffect(() => {
    const handleFocus = () => setError(null);
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  return (
    <Box sx={{ width: '100%', maxWidth: 'calc(100vw - 48px)', overflow: 'hidden', mx: 'auto', boxSizing: 'border-box' }}>
      {/* Error message display */}
      {error && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
          <Typography variant="body2">{error}</Typography>
        </Box>
      )}
      
      {/* Back button container when viewing results */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        
        {/* Back button when viewing results */}
        {viewingResults && (
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToJobs}
          >
            Back to Jobs
          </Button>
        )}
      </Box>
      
      {/* Results View or Jobs List */}
      {viewingResults ? (
        resultsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ResearchResults 
            jobId={selectedJobId}
            results={jobResults}
          />
        )
      ) : (
        <Paper 
          elevation={0}
          sx={{ 
            p: 0, 
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            width: '100%',
            boxSizing: 'border-box',
            margin: '0 auto',
            border: 'none'
          }}
        >
          <Box sx={{ 
            overflow: 'auto',
            flex: 1,
            width: '100%',
            maxWidth: '100%',
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '4px'
            }
          }}>
            <Paper elevation={0} sx={{ p: 3, width: '100%', maxWidth: '100%', overflow: 'hidden', border: 'none' }}>
              {jobs.length === 0 ? (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    No research jobs found. Create a new research job to get started.
                  </Typography>
                </Box>
              ) : (
                <ResearchJobsTable 
                  jobs={jobs}
                  isLoading={false}
                  onViewResults={handleViewResults}
                  onPauseJob={handlePauseJob}
                  onResumeJob={handleResumeJob}
                  onDeleteJob={handleDeleteJob}
                />
              )}
            </Paper>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default ResearchAgentActivityHistory;
